import { Injectable } from '@angular/core';
import { AuthService, User } from '@auth0/auth0-angular';

import * as auth0 from 'auth0-js';
import { catchError, Observable, of, Subject, switchMap, takeUntil, tap, throwError } from 'rxjs';

import { DataService } from '../data/data.service';
import { HttpService } from '../http/http.service';

import { ICheckEmailResponse, ICreateAccountError, ICreateAccountPayload, ICreateAccountRequest, ICreateAccountResponse, IProfileData } from '../../interfaces/IAuth';
import { environment } from '../../../environments/environment';
import { AUTH_REALM_CONFIG, AUTH_RESPONSE_TYPE } from './auth-data.config';
import { MessagesService } from '../messages/messages.service';
import { IMessage } from '../messages/messages.interface';
import { CHECK_EMAIL, CREATE_ACCOUNT, getApiUrlForParams } from '../../utils/api.util';
import { RouterService } from '../router/router.service';
import { APP_ROUTES } from '../../utils/route.constant';
import { FIRST_TIME_USER, LOGIN_BUTTON_CLICKED_FLAG, SELECTED_CONTRACT, SELECTED_CONTRACT_DATA } from '../../utils/constants';
import { Ga4Service } from '../ga4/ga4.service';
import { ACC_LOGGEDOUT, ALL_PAGES, ERR_EVENT } from '../ga4/ga4.constant';
import { CookieService } from '../cookie/cookie.service';

@Injectable({
  providedIn: 'root'
})
export class AuthDataService {
  private cleaner$ = new Subject<void>();
  
  private webAuth = {
    domain: environment.domain,
    clientID: environment.clientId,
    redirectUri: window.location.origin,
  };

  private webAuthWithToken = new auth0.WebAuth({
    ...this.webAuth,
    responseType: AUTH_RESPONSE_TYPE,
  });

  constructor(
    private auth: AuthService,
    private data: DataService,
    private http: HttpService,
    private messages: MessagesService,
    private routerService: RouterService,
    private gaService: Ga4Service,
    private cookieService: CookieService,
  ) {

    this.auth.isAuthenticated$
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (isAuthenticated: boolean) => {
          if (isAuthenticated) {
            this.cookieService.setMyAccountAccessedState(true);
            this.init();
            return;
          }
        },
      });
    this.checkForAuthErrors();
  }

  checkForAuthErrors(): void {
    this.auth.error$
      .pipe(takeUntil(this.cleaner$))
      .subscribe((error: any) => {
        if (error?.message.includes("Login required")) {
          // user is not logged in
          // redirect to login page
          this.routerService.goto(APP_ROUTES.LOGIN);
        } else if (error?.message.includes("Unknown or invalid refresh token") || error?.message.includes("Invalid authorization code")) {
          // something wrong with the authentication
          // redirect to session-timeout page
          error && this.routerService.goto(APP_ROUTES.SESSION_TIMEOUT);
        } else {
          // something is broken
          error && this.routerService.goto(APP_ROUTES.OOPS_PAGE);
        }
      });
  }

  init(): void {
    this.getUserData()
      .pipe(
        switchMap((user: User | null | undefined) => {
          if (!user) return of(null);
          return this.getProfileData()
        }),
        takeUntil(this.cleaner$)
      ).subscribe();
  }

  getUserData(): Observable<User | null | undefined> {
    return this.auth.user$
      .pipe(
        tap((user: User | null | undefined) => {
          if (user) {
            this.data.user$.next(user);
            this.data.sub = user?.sub || '';
            this.data.auth0UserId.next(this.data.sub?.split('|')[1]);
          }
        })
      );
  }

  getProfileData(): Observable<IProfileData|null> {
    const profileData = this.data.profileData$.getValue();
    return (profileData) ? of(profileData) : this.updateProfileData();
  }

  login(email: string, password: string): void {
    this.webAuthWithToken.login({
      realm: AUTH_REALM_CONFIG,
      email,
      password,
    }, (err, result) => {
      // Navigates to dashboard on successful login
      this.data.loginAttempted$.next(true);
      if (err) {
        sessionStorage.removeItem(LOGIN_BUTTON_CLICKED_FLAG);
        const errorMessage: IMessage = {
          severity: 'warn',
          text: 'Wrong email or password.',
          icon: 'pi pi-exclamation-triangle'
        };
        this.messages.addMessages(errorMessage);
        this.gaService.messageEvent(ERR_EVENT, errorMessage.text, ALL_PAGES);
      }
    });
  }

  logout(inactivity = false): void {
    const auth = new auth0.WebAuth(this.webAuth);

    // Clear sessionStorage
    sessionStorage.removeItem(SELECTED_CONTRACT);
    sessionStorage.removeItem(FIRST_TIME_USER);
    sessionStorage.removeItem(SELECTED_CONTRACT_DATA);
    
    // Clear user data from DataService
    this.data.user$.next(null);
    this.data.sub = undefined;
    this.data.auth0UserId.next('');
    
    // Explicitly clear Auth0 tokens from localStorage (iOS Safari workaround)
    // On older iOS devices, auth.logout() may not properly clear cached tokens
    try {
      const clientId = environment.clientId;
      const keysToRemove: string[] = [];
      
      // Find all localStorage keys that belong to Auth0
      // Auth0 stores tokens with patterns like: @@auth0spajs@@::clientId::...
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('auth0') || 
          key.includes('@@auth0') || 
          key.includes(clientId) ||
          key.startsWith('com.auth0')
        )) {
          keysToRemove.push(key);
        }
      }
      
      // Remove all Auth0-related keys
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      // If localStorage access fails, continue with auth0-js logout
      console.warn('Error clearing localStorage:', error);
    }
    
    // Call auth0-js logout (handles server-side session cleanup and redirect)
    auth.logout({
      returnTo: `${window.location.origin}/${inactivity ? APP_ROUTES.SESSION_TIMEOUT : APP_ROUTES.LOGIN}`,
      clientID: environment.clientId,
    });
  }

  resetPassword(email: string): void {
    this.webAuthWithToken.changePassword({
      connection: AUTH_REALM_CONFIG,
      email,
    }, (err, resp) => {
      this.data.resetEmailSent$.next({ error: !!err, message: resp || err });
    });
  }

  updateProfileData(): Observable<IProfileData|null> {
    if (!this.data.auth0UserId.getValue()) return of(null);

    return this.http.get<IProfileData>(
      `/api/${this.data.auth0UserId.getValue()}/profile`
    ).pipe(
      tap(response => {
        this.data.profileData$.next(response);
      }),
      catchError(() => {
        this.data.globalLoader$?.next(false);
        this.data.profileData$.next(null);
        this.data.profileDashboardApiErrorState$.next(true);
        return of(null);
      })
    );
  }

  checkEmail(email: string): Observable<ICheckEmailResponse> {
    return this.http.post<ICheckEmailResponse>(CHECK_EMAIL, { email: email });
  }

  createAcccount(request: ICreateAccountRequest): Observable<ICreateAccountResponse | ICreateAccountError> {
    const username = request.email.toLowerCase();
    const postData: ICreateAccountPayload = {
      username: username,
      password: request.password,
      attributes: {
        email: username.toLowerCase(),
        name: request.name, // optional
        contracts: request.contractDetails.map((contract) => {
          return  {
            'custom:contract_clientId': contract.clientId,
            'custom:contract_number': contract.contract,
            'custom:coverageGroup': contract.coverageGroup,
            'custom:clientSpecificProductName': contract.clientSpecificProductName,
            'custom:site_id': '49516', // TODO: this value should be dynamic base on the current site
          }
        }),      
        // other custom attributes
        'version': 'MA3'
      }
    }
    return this.http.post<ICreateAccountResponse | ICreateAccountError>(CREATE_ACCOUNT, postData);
  }

  /**
   * Cleanup method to unsubscribe from all subscriptions
   * Call this method when the service needs to be cleaned up
   * (e.g., from app component's ngOnDestroy)
   */
  cleanup(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
  }
}
